
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ContentCalendarView } from "@/components/content-calendar/ContentCalendarView";
import { useContentCalendar } from "@/hooks/useContentCalendar";
import { startOfMonth, endOfMonth, addDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Filter } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ViewStateManager } from "@/state/view/ViewStateManager";
import { Loader2 } from "lucide-react";

const BlogCalendar = () => {
  useDocumentTitle("Blog Calendar");
  const navigate = useNavigate();
  const viewStateManager = ViewStateManager.getInstance();
  
  // Load saved state from ViewStateManager if available
  const savedState = viewStateManager.getViewState("/dashboard/blog/calendar") || {};
  
  // State management
  const [currentDate, setCurrentDate] = useState<Date>(
    savedState.currentDate ? new Date(savedState.currentDate) : new Date()
  );
  const [viewMode, setViewMode] = useState<"list" | "month" | "week" | "day">(
    savedState.viewMode || "month"
  );
  
  // Save state changes to ViewStateManager
  useEffect(() => {
    viewStateManager.updateViewState("/dashboard/blog/calendar", {
      currentDate: currentDate.toISOString(),
      viewMode
    });
  }, [currentDate, viewMode]);
  
  // Navigation handlers
  const goToNextMonth = () => setCurrentDate(prev => new Date(prev.setMonth(prev.getMonth() + 1)));
  const goToPrevMonth = () => setCurrentDate(prev => new Date(prev.setMonth(prev.getMonth() - 1)));
  const goToToday = () => setCurrentDate(new Date());
  
  // Set date range for fetching data
  const startDate = addDays(startOfMonth(currentDate), -15);
  const endDate = addDays(endOfMonth(currentDate), 15);
  
  // Fetch only blog posts
  const { data, isLoading } = useContentCalendar({
    startDate,
    endDate,
    contentType: 'blog',
    filters: {}
  });
  
  // Handle view mode change
  const handleViewModeChange = (mode: "list" | "month" | "week" | "day") => {
    setViewMode(mode);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Blog Calendar</h1>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          
          <div className="flex">
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-r-none px-2"
              onClick={goToPrevMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-l-none px-2"
              onClick={goToNextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Button 
            size="sm" 
            onClick={() => navigate("/dashboard/blog/posts/new")}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Post
          </Button>
        </div>
      </div>
      
      <Card className="p-6">
        <h2 className="text-lg font-medium mb-4">
          {format(currentDate, "MMMM yyyy")}
        </h2>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
          </div>
        ) : data ? (
          <ContentCalendarView 
            items={data.items}
            currentDate={currentDate}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onNewContent={() => navigate("/dashboard/blog/posts/new")}
          />
        ) : (
          <div className="flex items-center justify-center p-12 text-muted-foreground">
            No blog posts found for the selected period
          </div>
        )}
      </Card>
    </div>
  );
};

export default BlogCalendar;
